import { useContext, useEffect, useMemo, useState } from 'react';
// TODO: switch to vertical view - https://github.com/daniel-hauser/react-organizational-chart/pull/66
// TODO: https://mui.com/x/react-data-grid/
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';

import { Button, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import {
  SortingFn,
  SortingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import classNames from 'classnames';
import { format } from 'date-fns';
import { deleteOrganisationUser, updateOrganisationUserRole } from '../api/organisations';
import { AccountContext } from '../context/account';
import { OrganisationContext } from '../context/organisation';
import { OrganisationMemberDetails, UserRoles } from '../core/global';
import { createOrgUserShape, roleOptions } from '../data/form-shapes';
import { getItemByKeyValue } from '../helpers/core';
import styles from "../styles/components/organisation-users.module.scss";
import tableStyles from "../styles/components/table.module.scss";
import { CopyButton } from './copy-button';
import { ErrorMessage, WarningMessage } from './error-message';
import { CreateForm } from './form-components';
import Loader from './loader';

const columnHelper = createColumnHelper<OrganisationMemberDetails>()

// https://tanstack.com/table/v8/docs/framework/react/examples/sorting
//custom sorting logic for one of our enum columns
const sortStatusFn: SortingFn<any> = (rowA, rowB, _columnId) => {
  const statusA = rowA.original.role
  const statusB = rowB.original.role
  const statusOrder = ['admin', 'external-user', 'member']
  return statusOrder.indexOf(statusA) - statusOrder.indexOf(statusB)
}

interface OrganisationUsersProps {
  id: number | string | null;
}

export const OrganisationUsers = ({id}: OrganisationUsersProps) => {
  const [sorting, setSorting] = useState<SortingState>([{id: 'email',desc: false}])

  const [showWarning, setShowWarning] = useState(false)
  const [addNewUser, setAddNewUser] = useState(false)
  const [errors, setErrors] = useState<string[]>([])
  const [data, _setData] = useState<OrganisationMemberDetails[]>([])
  
  const [deleteUser, setDeleteUser] = useState(false)
  const [newUserRole, setNewUserRole] = useState<UserRoles | null>()
  const [editedRowId, setEditedRowId] = useState<number | string | null>(null)

  const { loading, refetchOrganisationUsers, organisationUsers  } = useContext(OrganisationContext);
  
  const { account  } = useContext(AccountContext);

  const {uid} = account

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('email', {
        cell: info => <>
          {info.getValue()}
          <CopyButton content={info.getValue()} />
        </>,
        header: () => <span>E-mail</span>,
        // footer: info => info.column.id,
        sortingFn: 'alphanumeric'
      }),
      columnHelper.accessor(row => `${row.first_name} ${row.last_name}`, {
        id: 'Full Name',
        cell: info => <span>{info.getValue()}</span>,
        header: () => <span>Full Name</span>,
        // footer: info => info.column.id,
        sortingFn: 'alphanumeric'
      }),
      columnHelper.accessor('role', {
        header: () => <span>Role</span>,
        cell: (info) => {
          const isYourself = info.row.original.user_id === uid
          if (isYourself) {
            return (
              <span>{info.getValue()}</span>
            )
          }
          // const valueIsDifferent = info.getValue() !== 
          const role = info.getValue()
          const useValue = editedRowId === info.row.original.id && newUserRole && newUserRole !== role ? newUserRole : role
          return (
            <Select
              labelId="role-select"
              id="role-select"
              value={`${useValue}`}
              onChange={(event: SelectChangeEvent) => {
                  setEditedRowId(info.row.original.id)
                  setNewUserRole(event.target.value as UserRoles)
                  setDeleteUser(false)
                }
              }
            >
              {roleOptions.map(({key, value}) => {
                return <MenuItem key={key} value={key}>{value}</MenuItem>
              })}
            </Select>
          )
        },
        // footer: info => info.column.id,
        sortingFn: sortStatusFn
      }),
      columnHelper.accessor('last_login', {
        cell: info => {
          const dateValue = info.getValue()
          if (!dateValue) {
            
            const isActive = info.row.original.is_confirmed === true
            if (!isActive) {
              return <>Not activated</>
            }
            return <>Unknown</>
          }
          const useDate = format(new Date(dateValue), 'dd/MM/yy HH:mm');
          return `${useDate}`
        },
        header: () => <span>Last login</span>,
        // footer: info => info.column.id,
        sortingFn: 'datetime'
      }),
    ]
  }, [data, editedRowId, newUserRole])

  useEffect(() => {
    setErrors([])
    setAddNewUser(false)
    setShowWarning(false)
  }, [])

  useEffect(() => {
    if (organisationUsers && uid) {
      // const index = getItemByKeyValue(organisationUsers, 'user_id', uid, true)
      //  make active user the first person on the list
      // _setData([...organisationUsers.sort((a, b) => {
      //   if (a.user_id === uid) {
      //     return -1;
      //   } 
      //   return 1
      // })
      // ])

      _setData([...organisationUsers])

    }
  }, [organisationUsers, uid])

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    enableSorting: true,
    // sortingFns: {
    //   sortStatusFn, //or provide our custom sorting function globally for all columns to be able to use
    // },
  })

  useEffect(() => {
    refetchOrganisationUsers(id)
  }, [id]);
  
  if (loading) {
    return (
      <div className={styles.wrapper}>
        <Loader center />
      </div>
    )
  }

  return (
    <div className={styles.wrapper}>
      <ErrorMessage message={errors} />
      <table className={classNames(tableStyles.table, styles.table)}>
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th key={header.id} className={classNames(tableStyles.fieldName, {
                  [tableStyles.sortable]: header.column.getCanSort(),
                })}>
                  <div
                    onClick={header.column.getToggleSortingHandler()}
                    title={
                      header.column.getCanSort()
                        ? header.column.getNextSortingOrder() === 'asc'
                          ? 'Sort ascending'
                          : header.column.getNextSortingOrder() === 'desc'
                            ? 'Sort descending'
                            : 'Clear sort'
                        : undefined
                    }
                  >
                    <span>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: ' 🔼',
                          desc: ' 🔽',
                        }[header.column.getIsSorted() as string] ?? null}
                    </span>
                  </div>
                </th>
              ))}
              <th className={tableStyles.actions}>Actions</th>
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map(row => {
              const isYourself = row.original.user_id === uid
              const isEditUserRow = editedRowId !== null &&  editedRowId === row.original.id
              return (
                <tr key={row.id} className={classNames({
                  [tableStyles.highlightedRow]: isYourself,
                  [tableStyles.deleting]: isEditUserRow && deleteUser
                })}>
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id} className={tableStyles.fieldValue}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext()) || (
                        <span>
                          <em>Not provided</em>
                        </span>
                      )}
                    </td>
                  ))}
                  <td className={tableStyles.actions}>
                    
                    {!isYourself && 
                      <>
                        <Button onClick={() => {
                          setEditedRowId(row.original.id)
                          setDeleteUser(true)
                        }}>
                          <DeleteIcon />
                        </Button>

                        {isEditUserRow &&
                          <>
                            <div>
                              <Button 
                                size='small'
                                color='error'
                                startIcon={deleteUser? <DeleteForeverIcon /> : <QuestionMarkIcon />}
                                onClick={async () => {
                                  let apiResponse: any
                                  if (deleteUser) {
                                    apiResponse = await deleteOrganisationUser({id: editedRowId})
                                  } else {
                                    apiResponse =  await updateOrganisationUserRole({
                                      // user_id: row.original.user_id,
                                      role: newUserRole,
                                      organisation_id: row.original.organisation_id,
                                      email: row.original.email
                                    })
                                    
                                  }
                                  const {response, error, message} = apiResponse
                                  if (error !== 200) {
                                    setErrors( e => [`${message}`])
                                  } else {
                                    setErrors([])
                                    refetchOrganisationUsers(id)
                                  }
                                  setEditedRowId(null)
                                  setDeleteUser(false)
                                }}>
                                {deleteUser ? 'Remove' : 'Yes'}
                              </Button>
                              <Button 
                                size='small'
                                color="success"
                                onClick={() => {
                                  setEditedRowId(null)
                                  setDeleteUser(false)
                                }}>
                                Cancel
                              </Button>
                            </div>
                          </>
                        }
                      </>
                    }
                  </td>
                </tr>
              )
            }
          )}
        </tbody>
      </table>
      <div className={styles.newUser}>
        {addNewUser ? 
            <>
              <CreateForm
                className={styles.newUserForm}
                title={'Create a new organisation'}
                intro={<>Add user</>}
                isModal={false}
                inline
                shape={createOrgUserShape}
                onChange={(vals, key) => {
                  if (key === 'email') {
                    const {email} = vals
                    if (email) {
                      const found = getItemByKeyValue(data, 'email', email )
                      if (found !== -1) {
                        setShowWarning(true)
                      } else {
                        setShowWarning(false)
                      }
                    }
                  }
                }}
                successMessage="New user has been added"
                defaultValues={null}
                onCancel={() => {
                  setAddNewUser(false)
                }}
                onSuccess={() => {
                  setAddNewUser(false)
                  refetchOrganisationUsers(id)
                }}
                onSubmit={(vals ) => {
                  return updateOrganisationUserRole({...vals, organisation_id: id})
                }}
              />
              {showWarning && <WarningMessage message={['User is already on the account...']} />}
            </>
          :
          <>
            <Button onClick={() => {
              setAddNewUser(true)
              setShowWarning(false)
            }}>
              <AddIcon />Add user to organisation
            </Button>
          </>
        }
      </div>
    </div>
  );
}

